import { useState } from "react";
import { Minus, Plus, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { VOLUME_CATEGORIES, VOLUME_HEAVY_NOTICE, VOLUME_DISCLAIMER } from "@/constants/business";
import { useDialogs } from "@/context/DialogsContext";

const ALL_ITEMS = VOLUME_CATEGORIES.flatMap((c) => c.items);

export default function VolumeCalculator() {
  const { requestEstimate } = useDialogs();
  const [counts, setCounts] = useState({});
  const [manual, setManual] = useState("");

  const update = (key, delta) =>
    setCounts((prev) => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) + delta) }));

  const selected = ALL_ITEMS.filter((item) => (counts[item.key] || 0) > 0);
  const totalObjects = selected.reduce((sum, item) => sum + counts[item.key], 0);
  const manualVolume = parseFloat(manual.replace(",", ".")) || 0;
  const totalVolume = selected.reduce((sum, item) => sum + counts[item.key] * item.m3, 0) + manualVolume;
  const hasHeavy = selected.some((item) => item.heavy);

  const addToRequest = () => {
    const parts = selected.map((item) => `${counts[item.key]}× ${item.label}`);
    if (manualVolume > 0) parts.push(`Volume manuel : ${manualVolume.toFixed(1).replace(".", ",")} m³`);
    const summary = `${parts.join(", ")} (volume estimatif ≈ ${totalVolume.toFixed(1).replace(".", ",")} m³)`;
    requestEstimate({ type: "Groupage", objets: summary, source: "calculateur-volume" });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 md:p-7" data-testid="volume-calculator">
      <div className="grid lg:grid-cols-5 gap-6 lg:gap-10">
        <div className="lg:col-span-3">
          <h3 className="font-heading font-bold text-lg text-navy mb-1">Estimez votre volume</h3>
          <p className="text-xs text-muted-foreground mb-4">Ajoutez vos objets par catégorie pour obtenir un ordre de grandeur.</p>

          <Accordion type="multiple" defaultValue={["salon"]} className="space-y-2">
            {VOLUME_CATEGORIES.map((category) => {
              const categoryCount = category.items.reduce((sum, item) => sum + (counts[item.key] || 0), 0);
              return (
                <AccordionItem
                  key={category.key}
                  value={category.key}
                  data-testid={`volume-category-${category.key}`}
                  className="border border-gray-200 rounded-xl px-4"
                >
                  <AccordionTrigger className="text-sm font-heading font-semibold text-navy hover:no-underline py-3">
                    <span className="flex items-center gap-2">
                      {category.label}
                      {categoryCount > 0 && (
                        <span className="bg-orange/10 text-orange text-[11px] font-bold rounded-full px-2 py-0.5">
                          {categoryCount}
                        </span>
                      )}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-3">
                    <div className="space-y-2">
                      {category.items.map((item) => (
                        <div key={item.key} className="flex items-center justify-between gap-3">
                          <span className="text-sm text-navy flex items-center gap-1.5">
                            {item.label}
                            {item.heavy && <AlertTriangle className="w-3.5 h-3.5 text-copper" aria-label="Objet nécessitant une étude spécifique" />}
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              data-testid={`volume-minus-${item.key}`}
                              onClick={() => update(item.key, -1)}
                              aria-label={`Retirer ${item.label}`}
                              className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center text-navy hover:border-navy transition-colors"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="w-5 text-center text-sm font-semibold text-navy" data-testid={`volume-count-${item.key}`}>
                              {counts[item.key] || 0}
                            </span>
                            <button
                              type="button"
                              data-testid={`volume-plus-${item.key}`}
                              onClick={() => update(item.key, 1)}
                              aria-label={`Ajouter ${item.label}`}
                              className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center text-navy hover:border-navy transition-colors"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>

          <div className="flex items-center justify-between gap-3 mt-3 border border-gray-200 rounded-xl px-4 py-3">
            <span className="text-sm font-medium text-navy">Volume manuel (m³)</span>
            <Input
              data-testid="volume-manual-input"
              inputMode="decimal"
              placeholder="0"
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              className="w-24 h-9 rounded-lg text-right"
            />
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-graylight rounded-xl p-5 lg:sticky lg:top-24">
            <h4 className="font-heading font-semibold text-sm text-navy mb-3">Récapitulatif</h4>
            {selected.length === 0 && manualVolume === 0 ? (
              <p className="text-xs text-muted-foreground mb-4">Aucun objet sélectionné pour le moment.</p>
            ) : (
              <ul className="text-xs text-navy/80 space-y-1 mb-4 max-h-40 overflow-y-auto" data-testid="volume-recap-list">
                {selected.map((item) => (
                  <li key={item.key}>
                    {counts[item.key]}× {item.label}
                  </li>
                ))}
                {manualVolume > 0 && <li>Volume manuel : {manualVolume.toFixed(1).replace(".", ",")} m³</li>}
              </ul>
            )}

            <div className="flex items-baseline justify-between border-t border-gray-200 pt-3">
              <span className="text-sm font-semibold text-navy">Objets</span>
              <span className="font-heading font-bold text-lg text-navy" data-testid="volume-total-objects">{totalObjects}</span>
            </div>
            <div className="flex items-baseline justify-between mt-1 mb-3">
              <span className="text-sm font-semibold text-navy">Volume estimatif</span>
              <span className="font-heading font-extrabold text-2xl text-orange" data-testid="volume-total">
                ≈ {totalVolume.toFixed(1).replace(".", ",")} m³
              </span>
            </div>

            {hasHeavy && (
              <p className="flex items-start gap-2 text-xs text-navy bg-copper/10 border border-copper/30 rounded-lg px-3 py-2.5 mb-3" data-testid="volume-heavy-alert">
                <AlertTriangle className="w-4 h-4 text-copper shrink-0 mt-0.5" /> {VOLUME_HEAVY_NOTICE}
              </p>
            )}

            <Button
              data-testid="volume-add-to-request"
              onClick={addToRequest}
              disabled={selected.length === 0 && manualVolume === 0}
              className="w-full bg-navy hover:bg-navy/90 text-white rounded-full font-semibold"
            >
              Ajouter à ma demande
            </Button>
            <p className="text-[11px] text-muted-foreground mt-3">{VOLUME_DISCLAIMER}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
